/**
 * Voice-assisted prescription — client-side matching + field mapping (PRD.280).
 *
 * Pure, DOM-free logic so it is unit-testable under node:test and shared by the
 * prescription tab. It does NOT call any provider — it takes the medications a
 * backend has already extracted and resolves them against the SAME inventory
 * picker candidate list used by manual prescription entry.
 *
 * Safety (PRD.280 §Client-Side Inventory Matching):
 *   - only a UNIQUE normalized case-insensitive EXACT match auto-links;
 *   - no match → unresolved (free-text) item, `Match inventory` required;
 *   - multiple exact matches → ambiguous, never guessed;
 *   - NEVER fuzzy auto-link.
 * Normalization reuses `normalizeName` (the project's existing safe helper),
 * which lowercases and collapses punctuation/whitespace but PRESERVES digits so
 * clinically meaningful strength (e.g. "500 mg") is not stripped to force a match.
 */

import { normalizeName } from './allergy-check.js';

/** One medication returned by the backend extraction contract. */
export interface ExtractedMedication {
  medicineName: string;
  dose: string | null;
  schedule: string | null;
  duration: string | null;
  instructions: string | null;
}

/** Minimal inventory candidate needed for name matching. */
export interface InventoryMatchCandidate {
  inventoryItemId: string;
  /** The candidate display name — inventory_items.item_name (PRD.280 §7). */
  displayName: string;
}

export type VoiceMatchStatus = 'matched' | 'unmatched' | 'ambiguous';

export interface ResolvedVoiceMedication {
  extracted: ExtractedMedication;
  matchStatus: VoiceMatchStatus;
  /** Set only when exactly one candidate matches. */
  inventoryItemId: string | null;
  /** Candidate ids when ambiguous (multiple exact matches). */
  candidateIds?: string[];
}

/**
 * Resolve one extracted medicine name against the candidate list using the
 * matching rules above. Never fuzzy; ambiguity is surfaced, not guessed.
 */
export function matchMedicationToInventory(
  medicineName: string,
  candidates: readonly InventoryMatchCandidate[],
): { matchStatus: VoiceMatchStatus; inventoryItemId: string | null; candidateIds?: string[] } {
  const needle = normalizeName(medicineName);
  if (!needle) return { matchStatus: 'unmatched', inventoryItemId: null };

  const matches = candidates.filter((c) => normalizeName(c.displayName) === needle);
  if (matches.length === 1) {
    return { matchStatus: 'matched', inventoryItemId: matches[0].inventoryItemId };
  }
  if (matches.length === 0) {
    return { matchStatus: 'unmatched', inventoryItemId: null };
  }
  return {
    matchStatus: 'ambiguous',
    inventoryItemId: null,
    candidateIds: matches.map((m) => m.inventoryItemId),
  };
}

/** Resolve every extracted medication against the candidate list. */
export function resolveVoiceMedications(
  medications: readonly ExtractedMedication[],
  candidates: readonly InventoryMatchCandidate[],
): ResolvedVoiceMedication[] {
  return medications.map((extracted) => {
    const m = matchMedicationToInventory(extracted.medicineName, candidates);
    return { extracted, ...m };
  });
}

/** Fields the prescription draft uses for dose/schedule/duration/instructions. */
export interface VoiceDraftFields {
  doseQuantity: string;
  scheduleMorning: boolean;
  scheduleAfternoon: boolean;
  scheduleNight: boolean;
  /** '' | 'before_food' | 'after_food' | 'with_food' | 'empty_stomach' | 'anytime' */
  intakeTiming: string;
  durationValue: string; // string for empty-state ergonomics (matches the draft model)
  durationUnit: string; // 'days' | 'weeks' | 'months'
  instructions: string;
}

const WORD_NUMBERS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

const DURATION_RE =
  /^\s*(\d{1,4}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*(day|days|week|weeks|month|months|d|w|wk|wks|mo|mos)\s*$/i;

/** Normalize a matched duration unit token to the draft's enum values. */
function normalizeDurationUnit(token: string): 'days' | 'weeks' | 'months' {
  const t = token.toLowerCase();
  if (t.startsWith('w')) return 'weeks';
  if (t.startsWith('mo')) return 'months';
  return 'days';
}

interface ParsedSchedule {
  morning: boolean;
  afternoon: boolean;
  night: boolean;
  /** True when at least one time slot could be derived from the text. */
  matched: boolean;
}

/**
 * Parse a spoken schedule/frequency phrase into the three daily time slots the
 * prescription grid exposes (Morning / Afternoon / Night). Explicit time-of-day
 * words win; otherwise a recognised frequency (once/twice/thrice/…) expands to
 * the conventional slots. Unrecognised text yields `matched:false` so the caller
 * can preserve it verbatim in instructions instead of silently dropping it.
 */
export function parseSchedule(raw: string | null | undefined): ParsedSchedule {
  const t = ` ${(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()} `;
  if (t.trim().length === 0)
    return { morning: false, afternoon: false, night: false, matched: false };

  let morning = /\b(morning|mane|breakfast|forenoon)\b/.test(t);
  let afternoon = /\b(afternoon|noon|midday|lunch)\b/.test(t);
  let night = /\b(night|evening|bedtime|nocte|dinner|supper)\b/.test(t);

  if (!morning && !afternoon && !night) {
    if (/\b(three times|thrice|tds|tid|3 times)\b/.test(t)) {
      morning = afternoon = night = true;
    } else if (/\b(four times|qid|qds|4 times)\b/.test(t)) {
      morning = afternoon = night = true;
    } else if (/\b(twice|two times|2 times|bd|bid)\b/.test(t)) {
      morning = night = true;
    } else if (/\b(once|one time|1 time|od|qd|daily|every day|per day|a day)\b/.test(t)) {
      morning = true; // once-daily convention → morning slot
    }
  }

  return { morning, afternoon, night, matched: morning || afternoon || night };
}

/**
 * Derive the intake-timing dropdown value from the combined schedule +
 * instructions text. Only sets a value when clearly stated (never guesses
 * 'anytime').
 */
export function parseIntakeTiming(text: string): string {
  const t = text.toLowerCase();
  if (/empty stomach|before breakfast on an empty/.test(t)) return 'empty_stomach';
  if (/before (food|meal|meals|eating|breakfast|lunch|dinner)/.test(t)) return 'before_food';
  if (/after (food|meal|meals|eating|breakfast|lunch|dinner)/.test(t)) return 'after_food';
  if (/with (food|meal|meals|milk)/.test(t)) return 'with_food';
  return '';
}

/**
 * Map an extracted medication's free-text directions into the structured
 * prescription draft fields.
 *
 * The prescription schema has NO free-text schedule field and NO free-text
 * duration field (PRD.280 §11 finding). Mapping:
 *   - dose        → dose_quantity (free text; "500 mg", "1 capsule", …);
 *   - schedule    → parsed into Morning/Afternoon/Night slots + intake timing;
 *                   only unrecognised schedule text falls back into instructions;
 *   - duration    → parsed into duration_value + duration_unit ("N days|weeks|
 *                   months", digit or word number); otherwise folded verbatim;
 *   - instructions→ instructions (also scanned for intake timing).
 * Folded fallbacks are joined with the app's ' · ' separator so nothing is lost.
 */
export function extractedMedicationToDraftFields(med: ExtractedMedication): VoiceDraftFields {
  const doseQuantity = (med.dose ?? '').trim();

  // Schedule → time slots + timing.
  const scheduleRaw = (med.schedule ?? '').trim();
  const schedule = parseSchedule(scheduleRaw);

  // Duration → structured value/unit, else keep verbatim.
  let durationValue = '';
  let durationUnit = 'days';
  let leftoverDuration: string | null = null;
  const durationRaw = (med.duration ?? '').trim();
  if (durationRaw) {
    const match = DURATION_RE.exec(durationRaw);
    if (match) {
      const token = match[1].toLowerCase();
      const n = WORD_NUMBERS[token] ?? parseInt(token, 10);
      durationValue = String(n);
      durationUnit = normalizeDurationUnit(match[2]);
    } else {
      leftoverDuration = durationRaw;
    }
  }

  const instructionsRaw = (med.instructions ?? '').trim();
  const intakeTiming = parseIntakeTiming(`${scheduleRaw} ${instructionsRaw}`);

  const instructionParts = [
    // Only fall back to instructions when the schedule text was NOT understood.
    schedule.matched ? '' : scheduleRaw,
    leftoverDuration ?? '',
    instructionsRaw,
  ].filter((p) => p.length > 0);

  return {
    doseQuantity,
    scheduleMorning: schedule.morning,
    scheduleAfternoon: schedule.afternoon,
    scheduleNight: schedule.night,
    intakeTiming,
    durationValue,
    durationUnit,
    instructions: instructionParts.join(' · '),
  };
}
